import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { authAPI } from 'service';

const ForgotPasswordForm = ({ onBackToLogin }) => {
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'success'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateEmail = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;

    setLoading(true);
    const result = await authAPI.requestOtp(email, 'password');
    setLoading(false);

    if (result.success) {
      setStep('otp');
    } else {
      setErrors({ submit: result.error || 'Failed to send reset link' });
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    if (!otp) {
      setErrors({ otp: 'OTP is required' });
      return;
    }

    setLoading(true);
    const result = await authAPI.verifyOtp(email, otp, 'password');
    setLoading(false);

    if (result.success) {
      setStep('success');
    } else {
      setErrors({ otp: result.error || 'Invalid OTP' });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const newPassword = e.target['new-password'].value;
    const confirmPassword = e.target['confirm-password'].value;

    if (newPassword !== confirmPassword) {
      setErrors({ confirm: 'Passwords do not match' });
      return;
    }

    setLoading(true);
    const result = await authAPI.resetPassword(email, otp, newPassword);
    setLoading(false);

    onBackToLogin();


    if (result.success) {
      setStep('success');
    } else {
      setErrors({ submit: result.error || 'Failed to reset password' });
    }
  };

  const renderEmailForm = () => (
    <form onSubmit={handleEmailSubmit} className="space-y-6">
      <div className="text-center">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Forgot Password?</h2>
        <p className="text-sm text-gray-600">
          Enter your email address and we'll send you a verification code.
        </p>
      </div>
      <div>
        <label htmlFor="reset-email" className="block mb-1 text-sm font-medium text-gray-700">
          Email Address
        </label>
        <Input
          id="reset-email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors({});
          }}
          placeholder="Enter your email"
          className={errors.email ? 'border-red-500 focus:border-red-500' : ''}
          disabled={loading}
        />
        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
      </div>
      {errors.submit && (
        <div className="p-3 border border-red-200 rounded-md bg-red-50">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}
      <Button type="submit" fullWidth loading={loading}>
        Send OTP
      </Button>
      <div className="text-center">
        <button
          type="button"
          onClick={onBackToLogin}
          className="text-sm font-medium text-primary hover:text-primary/80"
        >
          Back to Sign In
        </button>
      </div>
    </form>
  );

  const renderOtpForm = () => (
    <form onSubmit={handleOtpSubmit} className="space-y-6 text-center">
      <h2 className="text-xl font-semibold text-gray-900">Verify OTP</h2>
      <p className="mb-4 text-sm text-gray-600">
        Enter the OTP sent to <span className="font-medium">{email}</span>
      </p>
      <Input
        id="otp"
        name="otp"
        type="text"
        value={otp}
        onChange={(e) => {
          setOtp(e.target.value);
          setErrors({});
        }}
        placeholder="Enter 6-digit OTP"
        className={errors.otp ? 'border-red-500 focus:border-red-500' : ''}
        disabled={loading}
      />
      {errors.otp && <p className="text-sm text-red-600">{errors.otp}</p>}

      <Button type="submit" fullWidth loading={loading}>
        Verify OTP
      </Button>
      <button
        type="button"
        onClick={() => setStep('email')}
        className="mt-2 text-sm text-primary hover:underline"
        disabled={loading}
      >
        Back
      </button>
    </form>
  );

  const renderSuccess = () => (
    <div className="space-y-4 text-center">
      <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
        <Icon name="CheckCircle" size={32} className="text-green-600" />
      </div>
      <div>
        <h3 className="mb-2 text-lg font-semibold text-gray-900">Verified Successfully</h3>
        <p className="text-sm text-gray-600">
          You can now reset your password or go back to sign in.
        </p>
      </div>
      <form className="space-y-4" onSubmit={handleResetPassword}>
        <Input
          id="new-password"
          name="new-password"
          type="password"
          placeholder="Enter new password"
          className="w-full"
          required
        />
        <Input
          id="confirm-password"
          name="confirm-password"
          type="password"
          placeholder="Confirm new password"
          className="w-full"
          required
        />
        {errors.submit && (
          <div className="p-3 border border-red-200 rounded-md bg-red-50">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}
        {errors.otp && <p className="text-sm text-red-600">{errors.otp}</p>}
        <Button type="submit" variant="primary" fullWidth>
          Reset Password
        </Button>
      </form>
      <Button type="button" onClick={onBackToLogin} variant="outline" className="w-full">
        Back to Sign In
      </Button>
    </div>
  );

  return (
    <>
      {step === 'email' && renderEmailForm()}
      {step === 'otp' && renderOtpForm()}
      {step === 'success' && renderSuccess()}
    </>
  );
};

export default ForgotPasswordForm;
